import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'

/**
 * Entrance variants. The matching rules live in shared/styles/marketing.css and
 * are selected by the `data-kq` attribute rather than by concatenating a class
 * name, so an unknown variant degrades to a plain fade instead of emitting a
 * class that does not exist.
 */
export type RevealVariant = 'rise' | 'left' | 'right' | 'zoom' | 'pop'

export interface RevealProps {
  children: ReactNode
  /** Extra classes for the wrapper. It IS the grid/flex item where used as one. */
  className?: string
  /** Stagger, in ms. Read by both the reveal transition and the ambient loops. */
  delay?: number
  variant?: RevealVariant
}

/*
 * ONE observer for the whole page, not one per element.
 *
 * The landing page reveals roughly thirty nodes. Thirty IntersectionObserver
 * instances all watching the same viewport means thirty separate callback queues
 * for information a single observer already has — the kind of cost that only
 * shows up on the low-end Android hardware this product ships to.
 *
 * Targets are unobserved the moment they fire: the entrance is a one-shot, so a
 * target still registered after its reveal is pure overhead.
 */
let sharedObserver: IntersectionObserver | null = null
const pending = new Map<Element, () => void>()

function observe(el: Element, onEnter: () => void): () => void {
  if (!sharedObserver) {
    sharedObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue
          pending.get(entry.target)?.()
          pending.delete(entry.target)
          sharedObserver?.unobserve(entry.target)
        }
      },
      {
        /* Fire slightly before the element is fully on screen, so the motion is
           already settling by the time it reaches a comfortable reading position
           instead of only starting there. */
        rootMargin: '0px 0px -10% 0px',
        threshold: 0.12,
      },
    )
  }

  pending.set(el, onEnter)
  sharedObserver.observe(el)

  return () => {
    pending.delete(el)
    sharedObserver?.unobserve(el)
  }
}

/**
 * Wraps its children in an element that fades and slides in the first time it
 * scrolls into view.
 *
 * The reveal deliberately sits on a WRAPPER rather than on the card itself: the
 * cards already own `hover:-translate-y-1` and `group-hover:scale-110`, and a
 * reveal that also wrote `transform` on that element would overwrite them.
 */
export function Reveal({ children, className = '', delay = 0, variant = 'rise' }: RevealProps) {
  const ref = useRef<HTMLDivElement>(null)
  const [shown, setShown] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    /*
     * No IntersectionObserver (a very old browser, or a test environment that has
     * not stubbed it) means the content must be shown, not hidden forever.
     * Failing open is the only acceptable direction: the alternative is a blank
     * marketing page.
     */
    if (typeof IntersectionObserver === 'undefined') {
      setShown(true)
      return
    }

    return observe(el, () => setShown(true))
  }, [])

  return (
    <div
      ref={ref}
      data-kq={variant}
      className={`kq-reveal ${shown ? 'kq-in' : ''} ${className}`.trim()}
      style={delay ? ({ '--kq-delay': `${delay}ms` } as CSSProperties) : undefined}
    >
      {children}
    </div>
  )
}
